import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  FileText,
  FolderKanban,
  PenSquare,
  Send,
  Upload,
} from 'lucide-react';

import Button from '../../components/ui/Button/Button';
import {
  InstructorRadarChart,
  type InstructorRadarChartDataset,
} from '../../components/ui/Charts/InstructorRadarChart';
import DimensionFeedbackCard from '../../components/ui/DimensionFeedbackCard/DimensionFeedbackCard';
import DimensionLabel from '../../components/ui/DimensionLabel/DimensionLabel';
import ChatInterface, {
  type ChatMessage,
} from '../../components/ui/ChatInterface/ChatInterface';
import ComparisonSelector from '../../components/layout/ComparisonSelector/ComparisonSelector';
import type { DropdownOption } from '../../components/ui/Dropdown/Dropdown';
import {
  projectService,
  type Project,
  type SubmissionSummary,
} from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import {
  feedbackService,
  type ComparisonAnalysis,
  type FeedbackResponse,
} from '../../services/feedbackService';
import { formatEnglishDateTime } from '../../lib/dateFormat';
import { getSubmissionFileDisplayNames } from '../../lib/submissionFiles';
import { waitForSubmissionProcessing } from '../../services/submissionProcessingService';

import './ProjectDetail.css';

/** ------------------------------- Mock AI Feedback ------------------------------- */
interface DimensionFeedback {
  id: number;
  name: string;
  score: number;
  maxScore: number;
  feedback: string;
  color: string;
}

interface AIFeedback {
  overallScore: number;
  maxScore: number;
  dimensions: DimensionFeedback[];
  summary: string;
  strengths: string[];
  improvements: string[];
  generatedAt: string;
}

interface ProcessingStage {
  atMs: number;
  label: string;
  progress: number;
}

type SubmissionOverlayState =
  | 'processing'
  | 'completed'
  | 'failed'
  | 'timeout';

const RESUBMISSION_MAX_FILE_SIZE = 50 * 1024 * 1024;

const radarDimensionLabelById: Record<number, string> = {
  1: 'Problem Framing',
  2: 'Stakeholders',
  3: 'Disciplinary Range',
  4: 'Reasoning',
  5: 'Credibility',
  6: 'Integ. Number',
  7: 'Integ. Depth',
  8: 'Social Impact',
  9: 'Limitations',
};

const getFirstName = (name?: string) => name?.trim().split(/\s+/)[0] || '';

const getSubmissionReportUrl = (projectId: string, submissionId?: string | null) => {
  const params = submissionId ? `?submissionId=${encodeURIComponent(submissionId)}` : '';

  return `/report/${projectId}${params}`;
};

const submissionProcessingStages: ProcessingStage[] = [
  { atMs: 0, progress: 8, label: 'Parsing submission...' },
  { atMs: 1200, progress: 28, label: 'Extracting disciplinary signals...' },
  { atMs: 3500, progress: 52, label: 'Scoring 9 InterQ dimensions...' },
  { atMs: 7000, progress: 78, label: 'Writing feedback anchors...' },
  { atMs: 12000, progress: 96, label: 'Finalising your report...' },
];

/** ------------------------------- Component ------------------------------- */
const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [project, setProject] = useState<Project | null>(null);
  const [aiFeedback, setAIFeedback] = useState<AIFeedback | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetryingEvaluation, setIsRetryingEvaluation] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [pageError, setPageError] = useState<string | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Submission list state
  const [submissions, setSubmissions] = useState<SubmissionSummary[]>([]);
  const [currentIteration, setCurrentIteration] = useState<number>(1);
  const [totalSubmissions, setTotalSubmissions] = useState<number>(1);

  // Comparison state
  const [leftSubmission, setLeftSubmission] = useState<string>("");
  const [rightSubmission, setRightSubmission] = useState<string>("");
  const [comparisonAnalysis, setComparisonAnalysis] =
    useState<ComparisonAnalysis | null>(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEssayText, setEditedEssayText] = useState<string>("");
  const [editedFiles, setEditedFiles] = useState<File[]>([]);
  const [editedChangeNotes, setEditedChangeNotes] = useState('');
  const [isEditUploadDragging, setIsEditUploadDragging] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Submission content
  const [submissionContent, setSubmissionContent] = useState<string>("");

  // Instructor suggestion
  const [instructorSuggestion, setInstructorSuggestion] = useState<{
    suggestion_text: string;
    updated_at: string;
  } | null>(null);

  // Selected dimension for feedback display
  const [selectedDimension, setSelectedDimension] = useState<string | null>(
    null,
  );
  const [isSubmissionProcessingDialogOpen, setIsSubmissionProcessingDialogOpen] =
    useState(false);
  const [submissionOverlayState, setSubmissionOverlayState] =
    useState<SubmissionOverlayState>('processing');
  const [submissionOverlayProgress, setSubmissionOverlayProgress] = useState(0);
  const [submissionOverlayStageLabel, setSubmissionOverlayStageLabel] = useState(
    submissionProcessingStages[0].label,
  );
  const activeWaitSessionRef = useRef(0);
  const isMountedRef = useRef(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !isSubmissionProcessingDialogOpen ||
      submissionOverlayState !== 'processing'
    ) {
      return;
    }

    setSubmissionOverlayProgress(submissionProcessingStages[0].progress);
    setSubmissionOverlayStageLabel(submissionProcessingStages[0].label);

    const timers = submissionProcessingStages
      .slice(1)
      .map((stage) =>
        window.setTimeout(() => {
          setSubmissionOverlayProgress(stage.progress);
          setSubmissionOverlayStageLabel(stage.label);
        }, stage.atMs),
      );

    return () => {
      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });
    };
  }, [isSubmissionProcessingDialogOpen, submissionOverlayState]);

  const handleCloseProcessingDialog = () => {
    activeWaitSessionRef.current += 1;
    setIsSubmissionProcessingDialogOpen(false);
    setSubmissionOverlayState('processing');
    setSubmissionOverlayProgress(0);
    setSubmissionOverlayStageLabel(submissionProcessingStages[0].label);
  };

  const buildAIFeedback = useCallback(
    (feedbackData: FeedbackResponse): AIFeedback => {
      const sub = feedbackData.submission;
      const dims = feedbackData.dimensions || [];

      return {
        overallScore:
          Math.round(
            (dims.reduce((sum, d) => sum + (d.ai_score || 0), 0) /
              (dims.length || 1)) *
              10,
          ) / 10,
        maxScore: 3,
        dimensions: dims.map((d) => ({
          id: d.dimension_id,
          name: d.dimension_label,
          score: d.ai_score || 0,
          maxScore: 3,
          feedback: d.ai_reasoning || "Analysis in progress...",
          color: d.dimension_color,
        })),
        summary: sub.ai_overall_summary || "Analysis complete",
        strengths: sub.ai_overall_strengths || [],
        improvements: sub.ai_priority_improvements || [],
        generatedAt: sub.submitted_at,
      };
    },
    [],
  );

  const applyFeedbackResponse = useCallback(
    (feedbackData: FeedbackResponse): void => {
      const sub = feedbackData.submission;
      const dims = feedbackData.dimensions || [];

      if (sub.essay_text) {
        setSubmissionContent(sub.essay_text);
      } else if (sub.file_urls && sub.file_urls.length > 0) {
        const fileNames = getSubmissionFileDisplayNames(
          sub.file_urls,
          sub.file_names,
        );
        setSubmissionContent(
          `[File submission: ${sub.file_urls.length} file(s)]\n${fileNames.join(
            "\n",
          )}`,
        );
      }

      setInstructorSuggestion(sub.instructor_suggestion ?? null);

      if (sub.iteration_number) {
        setCurrentIteration(sub.iteration_number);
      }
      if (sub.total_submissions) {
        setTotalSubmissions(sub.total_submissions);
      }
      if (sub.comparison_analysis) {
        setComparisonAnalysis(sub.comparison_analysis);
      }

      if (sub.ai_processing_status === 'completed' && dims.length > 0) {
        setAIFeedback(buildAIFeedback(feedbackData));
        setProcessingStatus('Analysis complete!');
        if (isSubmissionProcessingDialogOpen) {
          setSubmissionOverlayState('completed');
          setSubmissionOverlayProgress(100);
          setSubmissionOverlayStageLabel('Report ready');
        }
        setProject((prev) =>
          prev
            ? {
                ...prev,
                status: 'Completed',
              }
            : prev,
        );
        setSubmissionError(null);
        return;
      }

      if (sub.ai_processing_status === 'failed') {
        setProcessingStatus('Analysis failed.');
        if (isSubmissionProcessingDialogOpen) {
          setSubmissionOverlayState('failed');
          setSubmissionOverlayStageLabel('Evaluation failed');
        }
        setProject((prev) =>
          prev
            ? {
                ...prev,
                status: 'Failed',
                ai_processing_error: sub.ai_processing_error,
              }
            : prev,
        );
        setSubmissionError(
          `Analysis failed: ${sub.ai_processing_error || 'Unknown error'}`,
        );
        return;
      }

      if (sub.ai_processing_status === 'processing') {
        setProcessingStatus('AI is analyzing your submission...');
        setProject((prev) =>
          prev
            ? {
                ...prev,
                status: 'Processing',
              }
            : prev,
        );
        return;
      }

      if (sub.ai_processing_status === 'pending') {
        setProcessingStatus('Queued for analysis...');
        setProject((prev) =>
          prev
            ? {
                ...prev,
                status: 'Processing',
              }
            : prev,
        );
      }
    },
    [buildAIFeedback, isSubmissionProcessingDialogOpen],
  );

  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) return;

      try {
        setIsLoading(true);
        setPageError(null);
        setSubmissionError(null);
        const data = await projectService.getProject(projectId);
        setProject(data);

        // Load all submissions for this project
        const submissionsData =
          await projectService.getProjectSubmissions(projectId);
        setSubmissions(submissionsData.submissions);
        setTotalSubmissions(submissionsData.total_count);

        // Set up comparison selector options
        if (submissionsData.submissions.length >= 2) {
          const latestTwo = submissionsData.submissions.slice(-2);
          setLeftSubmission(latestTwo[0].id);
          setRightSubmission(latestTwo[1].id);
        }

        // If project has a submission, load its feedback (latest one)
        if (data.latest_submission_id) {
          console.log(
            "[ProjectDetail] Found submission:",
            data.latest_submission_id,
          );
          setSubmissionId(data.latest_submission_id);

          // Find the iteration number
          const latestSub = submissionsData.submissions.find(
            (s) => s.id === data.latest_submission_id,
          );
          if (latestSub) {
            setCurrentIteration(latestSub.iteration_number);
          }
        } else if (submissionsData.submissions.length > 0) {
          // Fallback: derive latest submission from the submissions list
          // This ensures all group team members can see feedback even if
          // latest_submission_id wasn't returned for them
          const lastSub =
            submissionsData.submissions[submissionsData.submissions.length - 1];
          console.log(
            "[ProjectDetail] Fallback: using last submission from list:",
            lastSub.id,
          );
          setSubmissionId(lastSub.id);
          setCurrentIteration(lastSub.iteration_number);
        }
      } catch (err) {
        console.error("Failed to load project:", err);
        setPageError('Failed to load project. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || isEditMode || isSubmissionProcessingDialogOpen) {
      return;
    }

    const completedSubmissionId =
      aiFeedback && submissionId
        ? submissionId
        : project?.status === 'Completed'
          ? submissionId || project.latest_submission_id
          : null;

    if (completedSubmissionId) {
      navigate(getSubmissionReportUrl(projectId, completedSubmissionId), {
        replace: true,
      });
    }
  }, [aiFeedback, isEditMode, isSubmissionProcessingDialogOpen, navigate, project, projectId, submissionId]);

  // Load AI feedback when available - single unified effect
  useEffect(() => {
    const loadFeedback = async () => {
      if (!submissionId) return;

      try {
        console.log(
          "[ProjectDetail] Loading feedback for submission:",
          submissionId,
        );
        const feedbackData = await feedbackService.getFeedback(submissionId);
        console.log("[ProjectDetail] Got feedback:", feedbackData);
        applyFeedbackResponse(feedbackData);
      } catch (err) {
        console.error("[ProjectDetail] Error loading feedback:", err);
      }
    };

    loadFeedback();
  }, [submissionId, applyFeedbackResponse]);

  // Poll for AI feedback when submission is processing/pending
  useEffect(() => {
    // Only poll if we have a submission ID, no feedback yet, and not failed/completed
    const shouldPausePollingForOverlay =
      isSubmissionProcessingDialogOpen && submissionOverlayState === 'processing';

    if (
      !submissionId ||
      aiFeedback ||
      shouldPausePollingForOverlay ||
      processingStatus.includes("failed") ||
      processingStatus.includes("complete")
    )
      return;

    let isActive = true;
    let pollInterval: NodeJS.Timeout;

    const checkFeedback = async () => {
      if (!isActive) return;

      try {
        const feedbackData = await feedbackService.getFeedback(submissionId);

        if (!isActive) return;

        const sub = feedbackData.submission;

        applyFeedbackResponse(feedbackData);

        if (sub.ai_processing_status === "processing") {
          setProcessingStatus("AI is analyzing your submission...");
          // Continue polling
          pollInterval = setTimeout(checkFeedback, 3000);
        } else if (sub.ai_processing_status === "pending") {
          setProcessingStatus("Queued for analysis...");
          // Continue polling
          pollInterval = setTimeout(checkFeedback, 5000);
        }
      } catch (err) {
        console.error("[ProjectDetail] Error polling feedback:", err);
        if (isActive) {
          // Retry after delay
          pollInterval = setTimeout(checkFeedback, 5000);
        }
      }
    };

    // Start polling
    checkFeedback();

    return () => {
      isActive = false;
      if (pollInterval) clearTimeout(pollInterval);
    };
  }, [
    submissionId,
    aiFeedback,
    processingStatus,
    isSubmissionProcessingDialogOpen,
    submissionOverlayState,
    applyFeedbackResponse,
  ]);

  // Handle project submission for AI evaluation
  const handleSubmitProject = async () => {
    if (!project || !projectId) return;

    // Confirm submission
    const confirmed = window.confirm(
      "Are you sure you want to submit this project for AI evaluation? This will change the status from Draft to Submitted. You'll be redirected to the project list.",
    );

    if (!confirmed) return;

    try {
      setIsSubmitting(true);
      setSubmissionError(null);

      const result = await projectService.submitProject(projectId);

      console.log("Submission result:", result);

      navigate(getSubmissionReportUrl(projectId, result.submission_id));
    } catch (err) {
      console.error("Failed to submit project:", err);
      let errorMessage = "Failed to submit project. Please try again.";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "object" && err !== null && "response" in err) {
        const errResponse = err as {
          response?: { data?: { error?: { message?: string } } };
        };
        errorMessage =
          errResponse.response?.data?.error?.message || errorMessage;
      }

      setSubmissionError(errorMessage);
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryEvaluation = async () => {
    if (!project || !projectId) return;

    const confirmed = window.confirm(
      "Retry AI evaluation for the latest submission?",
    );

    if (!confirmed) return;

    try {
      setIsRetryingEvaluation(true);
      setSubmissionError(null);
      setAIFeedback(null);
      setInstructorSuggestion(null);
      setProcessingStatus("Queued for analysis...");
      setProject((prev) =>
        prev
          ? {
              ...prev,
              status: "Processing",
              ai_processing_error: undefined,
            }
          : prev,
      );

      const result = await projectService.retryProjectEvaluation(projectId);
      setSubmissionId(result.submission_id);
    } catch (err) {
      console.error("Failed to retry evaluation:", err);
      let errorMessage = "Failed to retry evaluation. Please try again.";

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setSubmissionError(errorMessage);
      setProject((prev) =>
        prev
          ? {
              ...prev,
              status: "Failed",
            }
          : prev,
      );
    } finally {
      setIsRetryingEvaluation(false);
    }
  };

  // Handle edit mode toggle
  const handleEditClick = () => {
    // Always start with blank content — users submit a new version, not edit existing
    setEditedFiles([]);
    setEditedEssayText("");
    setEditedChangeNotes('');
    setSubmissionError(null);
    setIsEditMode(true);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedEssayText("");
    setEditedFiles([]);
    setEditedChangeNotes('');
    setIsEditUploadDragging(false);
  };

  const handleOpenEditFilePicker = () => {
    editFileInputRef.current?.click();
  };

  const validateEditedFile = async (file: File): Promise<string | null> => {
    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      return 'Only PDF files are supported for AI evaluation.';
    }

    if (file.size > RESUBMISSION_MAX_FILE_SIZE) {
      return 'PDF files must be 50 MB or smaller.';
    }

    try {
      const header = await file.slice(0, 5).text();
      if (header !== '%PDF-') {
        return 'This file does not look like a valid PDF. Please export or upload a PDF file.';
      }
    } catch {
      return 'Unable to read this PDF. Please try exporting it again and re-uploading.';
    }

    return null;
  };

  const handleEditedFileSelection = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    const [file] = files;

    if (!file) {
      return;
    }

    if (files.length > 1) {
      setSubmissionError('Upload one PDF file for each revision.');
      return;
    }

    const validationError = await validateEditedFile(file);
    if (validationError) {
      setSubmissionError(validationError);
      return;
    }

    setSubmissionError(null);
    setEditedFiles([file]);
  };

  const handleEditFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) {
      return;
    }

    void handleEditedFileSelection(event.target.files);
    event.target.value = '';
  };

  const handleEditUploadDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditUploadDragging(false);
    void handleEditedFileSelection(event.dataTransfer.files);
  };

  const handleRemoveEditedFile = () => {
    setEditedFiles([]);
  };

  // Handle resubmission with edited content
  const handleResubmit = async () => {
    if (!project || !projectId) return;

    const hasFiles = editedFiles.length > 0;
    const hasEssayText = !hasFiles && editedEssayText.trim().length > 0;

    if (!hasEssayText && !hasFiles) {
      setSubmissionError(
        'Add essay text or upload a PDF before submitting a new version.',
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      setSubmissionOverlayState('processing');
      setSubmissionOverlayProgress(submissionProcessingStages[0].progress);
      setSubmissionOverlayStageLabel(submissionProcessingStages[0].label);
      setIsSubmissionProcessingDialogOpen(true);

      const result = await projectService.resubmitProject(
        projectId,
        hasEssayText ? editedEssayText : undefined,
        hasFiles ? editedFiles : undefined,
        editedChangeNotes.trim() || undefined,
      );

      console.log("Resubmission result:", result);

      // Update state with new submission
      setSubmissionId(result.submission_id);
      setCurrentIteration(result.iteration_number);
      setTotalSubmissions(result.iteration_number);
      setProject((prev) =>
        prev
          ? {
              ...prev,
              status: "Processing",
              ai_processing_error: undefined,
            }
          : prev,
      );

      if (hasFiles && !hasEssayText) {
        setSubmissionContent(
          `[File submission: ${editedFiles.length} file(s)]\n${editedFiles
            .map((f) => f.name)
            .join("\n")}`,
        );
      } else {
        setSubmissionContent(editedEssayText);
      }

      setAIFeedback(null);
      setInstructorSuggestion(null);
      setProcessingStatus("Queued for analysis...");
      setEditedFiles([]);
      setEditedEssayText('');
      setEditedChangeNotes('');
      setIsEditMode(false);

      // Refresh submissions list
      const submissionsData =
        await projectService.getProjectSubmissions(projectId);
      setSubmissions(submissionsData.submissions);

      const waitSession = activeWaitSessionRef.current + 1;
      activeWaitSessionRef.current = waitSession;
      const waitResult = await waitForSubmissionProcessing(result.submission_id, {
        timeoutMs: 60000,
      });

      if (
        !isMountedRef.current ||
        activeWaitSessionRef.current !== waitSession
      ) {
        return;
      }

      if (waitResult.status === "completed") {
        const completedFeedback =
          waitResult.feedback ||
          (await feedbackService.getFeedback(result.submission_id));
        applyFeedbackResponse(completedFeedback);
        navigate(getSubmissionReportUrl(projectId, result.submission_id), {
          replace: true,
        });
      } else if (waitResult.status === "failed" && waitResult.feedback) {
        applyFeedbackResponse(waitResult.feedback);
        navigate(getSubmissionReportUrl(projectId, result.submission_id), {
          replace: true,
        });
      } else if (waitResult.status === 'timeout') {
        setSubmissionOverlayState('timeout');
        setSubmissionOverlayStageLabel('Still processing in the background');
        setProcessingStatus(
          'Analysis is taking longer than expected. You can stay here or come back later.',
        );
      }
    } catch (err) {
      console.error("Failed to resubmit project:", err);
      let errorMessage = "Failed to resubmit project. Please try again.";

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setIsSubmissionProcessingDialogOpen(false);
      setSubmissionOverlayState('processing');
      setSubmissionOverlayProgress(0);
      setSubmissionOverlayStageLabel(submissionProcessingStages[0].label);
      setSubmissionError(errorMessage);
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  // Build submissions options for comparison dropdown
  const submissionOptions: DropdownOption[] = submissions.map((sub) => ({
    id: sub.id,
    label: sub.submitted_by_name
      ? `Submission ${sub.iteration_number} (by ${sub.submitted_by_name})`
      : `Submission ${sub.iteration_number}`,
  }));

  // Prepare radar data from feedback (all dimensions)
  const radarDimensions = aiFeedback
    ? aiFeedback.dimensions.map((dim) => {
        console.log(
          "[ProjectDetail] Radar dimension:",
          dim.name,
          "score:",
          dim.score,
          "color:",
          dim.color,
        );
        return dim;
      })
    : [];
  const radarLabels = radarDimensions.map(
    (dimension) => radarDimensionLabelById[dimension.id] || dimension.name,
  );
  const radarDatasets: InstructorRadarChartDataset[] = radarDimensions.length > 0
    ? [
        {
          backgroundColor: 'rgba(24, 28, 98, 0.20)',
          borderColor: '#181C62',
          borderWidth: 2.2,
          data: radarDimensions.map((dimension) => dimension.score),
          label: 'Score',
          pointBackgroundColor: '#181C62',
          pointHoverRadius: 5,
          pointRadius: 3.5,
        },
      ]
    : [];

  console.log("[ProjectDetail] Radar data:", radarDimensions.length, "points");
  console.log("[ProjectDetail] aiFeedback exists:", !!aiFeedback);
  console.log(
    "[ProjectDetail] aiFeedback dimensions:",
    aiFeedback?.dimensions.length,
  );

  // Generate improvement suggestion based on score (1-3 scale)
  const getImprovementSuggestion = (
    dimensionName: string,
    score: number,
  ): string => {
    const suggestions: Record<string, Record<number, string>> = {
      "Frame the problem with an integrative approach": {
        1: "To reach level 2, start by clearly identifying the problem and acknowledging multiple perspectives. Begin exploring how different disciplines might view the issue differently.",
        2: "To get level 3, you need to demonstrate sophisticated integration of multiple disciplinary perspectives. Show how these perspectives interact and complement each other to frame a more complete understanding of the problem.",
      },
      "Stakeholder consideration": {
        1: "To reach level 2, identify the key stakeholders affected by the issue. Consider their diverse interests and how they might be impacted differently.",
        2: "To get level 3, conduct deeper analysis of stakeholder relationships and power dynamics. Consider both direct and indirect stakeholders, and analyze potential conflicts of interest and ethical implications.",
      },
      "Range of disciplinary perspectives": {
        1: "To reach level 2, incorporate perspectives from at least 2-3 different disciplines. Show awareness that multiple fields can contribute to understanding the issue.",
        2: "To get level 3, integrate 4 or more distinct disciplinary perspectives. Demonstrate deep understanding of how each discipline uniquely contributes to analyzing the problem.",
      },
      "Disciplinary reasoning": {
        1: "To reach level 2, begin using concepts and methods specific to different disciplines. Show basic understanding of how each discipline approaches problems.",
        2: "To get level 3, demonstrate sophisticated use of disciplinary reasoning. Apply discipline-specific methodologies accurately and explain the rationale behind choosing particular analytical approaches.",
      },
      "Credibility of disciplinary knowledge": {
        1: "To reach level 2, start citing credible sources from different disciplines. Include peer-reviewed research, expert opinions, or authoritative references.",
        2: "To get level 3, critically evaluate the quality and relevance of disciplinary sources. Discuss the strengths and limitations of different knowledge claims and explain why certain sources are more credible in specific contexts.",
      },
      "Number of disciplinary integration": {
        1: "To reach level 2, make explicit connections between at least two disciplines. Show how insights from one field relate to or inform another.",
        2: "To get level 3, create sophisticated integrations across multiple disciplines. Demonstrate how combining insights generates new understanding that wouldn't emerge from any single discipline alone.",
      },
      "Depth of disciplinary integration": {
        1: "To reach level 2, move beyond simply listing different perspectives. Begin synthesizing ideas by identifying common themes or complementary insights across disciplines.",
        2: "To get level 3, achieve deep integration where disciplinary insights are woven together seamlessly. Create new frameworks or solutions that transform understanding by combining disciplinary knowledge in novel ways.",
      },
      "Social (society) impact": {
        1: "To reach level 2, identify specific societal impacts of the issue. Consider effects on communities, institutions, or social structures.",
        2: "To get level 3, analyze societal impacts with nuance and depth. Consider short-term and long-term effects, intended and unintended consequences, and differential impacts on various social groups. Discuss ethical implications and potential solutions.",
      },
      Limitations: {
        1: "To reach level 2, acknowledge that your analysis has limitations. Identify gaps in your research or areas where more information would be valuable.",
        2: "To get level 3, provide thoughtful, specific discussion of limitations. Address methodological constraints, scope limitations, potential biases, and areas requiring further investigation. Show how these limitations might affect your conclusions.",
      },
    };

    // Generic suggestion if specific dimension not found
    const genericSuggestions: Record<number, string> = {
      1: "To reach level 2, you need to enhance the depth of your explanations. Add more detailed examples and dive deeper into the mechanisms and relationships within this dimension. Use more detailed reasoning to strengthen your arguments.",
      2: "To get level 3, demonstrate mastery by providing comprehensive analysis with sophisticated integration. Include concrete examples, consider multiple perspectives, and show how different elements interact. Back up your arguments with credible evidence and explain the implications of your analysis.",
    };

    if (score >= 3) return ""; // No suggestion needed for level 3

    return (
      suggestions[dimensionName]?.[score] || genericSuggestions[score] || ""
    );
  };

  // Handle dimension click from radar chart
  const handleDimensionClick = (dimension: string) => {
    console.log("[ProjectDetail] Dimension clicked:", dimension);
    console.log(
      "[ProjectDetail] Available dimensions:",
      aiFeedback?.dimensions.map((d) => d.name),
    );
    setSelectedDimension(dimension);
  };

  const handleRadarPointClick = (dimensionIndex: number) => {
    const dimension = radarDimensions[dimensionIndex];
    if (!dimension) {
      return;
    }

    handleDimensionClick(dimension.name);
  };

  // Handle chat message send
  const handleSendMessage = (message: string) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: "user",
      message,
      timestamp: new Date(),
    };

    setChatMessages([...chatMessages, newMessage]);
    setIsChatLoading(true);

    // Simulate LLM response (replace with actual API call)
    setTimeout(() => {
      const llmResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: "llm",
        message:
          "Thank you for your question. This is a mock response. In production, this would connect to the LLM API to provide contextual feedback about your submission.",
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, llmResponse]);
      setIsChatLoading(false);
    }, 1000);
  };

  // Submission content is now loaded directly in loadFeedback effect

  const projectTypeLabel =
    project?.project_type === 'group' ? 'Group Project' : 'Individual Project';
  const courseLabel = project?.course_code || 'Unassigned course';
  const nextSubmissionVersion = totalSubmissions + 1;
  const isPdfResubmission = editedFiles.length > 0;
  const canSubmitNewVersion =
    editedEssayText.trim().length > 0 || editedFiles.length > 0;
  const resubmitGridClassName = isPdfResubmission
    ? 'project-resubmit-grid project-resubmit-grid--single'
    : 'project-resubmit-grid';
  const submissionOverlayTitle =
    submissionOverlayState === 'completed'
      ? 'Your new report is ready'
      : submissionOverlayState === 'failed'
        ? 'AI evaluation failed'
        : submissionOverlayState === 'timeout'
          ? 'Still processing in the background'
          : 'AI is evaluating your submission';
  const evaluationScope = project?.ai_model === 'custom_hf'
    ? 'the Social impact dimension'
    : 'all 9 InterQ dimensions';
  const submissionOverlayDescription =
    submissionOverlayState === 'completed'
      ? `Version ${currentIteration} has been scored for ${evaluationScope}. Open the report to review the updated feedback.`
      : submissionOverlayState === 'failed'
        ? project?.ai_processing_error ||
          submissionError ||
          'We could not complete the evaluation for this submission.'
        : submissionOverlayState === 'timeout'
          ? 'The submission was accepted, but the report is taking longer than usual. You can stay on this page or come back later.'
          : `We're scoring your work for ${evaluationScope} and writing fresh feedback. This usually takes about a minute.`;

  const renderResubmissionComposer = () => (
    <div className="project-resubmit-page">
      <div className="project-resubmit-page__content">
        <header className="project-resubmit-page__header">
          <h1 className="project-resubmit-page__title">Submit New Version</h1>
          <p className="project-resubmit-page__subtitle">
            Add a new submission to &quot;{project?.title}&quot; — the previous
            versions stay in your history.
          </p>
        </header>

        {submissionError ? (
          <div className="project-resubmit-page__error">{submissionError}</div>
        ) : null}

        <section className="project-resubmit-context">
          <div className="project-resubmit-context__icon">
            <FolderKanban size={18} strokeWidth={2} />
          </div>
          <div className="project-resubmit-context__copy">
            <div className="project-resubmit-context__name">{project?.title}</div>
            <div className="project-resubmit-context__meta">
              <span>
                <strong>Course:</strong> {courseLabel}
              </span>
              <span>
                <strong>Type:</strong> {projectTypeLabel}
              </span>
              <span>
                <strong>ID:</strong> {project?.id}
              </span>
            </div>
          </div>
          <span className="project-resubmit-context__version">
            Submitting v{nextSubmissionVersion}
          </span>
        </section>

        <div className={resubmitGridClassName}>
          <section className="project-resubmit-card">
            <div className="project-resubmit-card__heading">
              <PenSquare size={18} strokeWidth={2} />
              <h2>Revision Details</h2>
            </div>

            <div className="project-resubmit-field">
              <label className="project-resubmit-field__label">Course</label>
              <div className="project-resubmit-field__readonly">{courseLabel}</div>
            </div>

            <div className="project-resubmit-field">
              <label className="project-resubmit-field__label">Project Name</label>
              <div className="project-resubmit-field__readonly">{project?.title}</div>
            </div>

            <div className="project-resubmit-field">
              <label className="project-resubmit-field__label">Project Type</label>
              <div className="project-resubmit-field__readonly">
                {projectTypeLabel}
              </div>
            </div>

            <div className="project-resubmit-field">
              <label
                className="project-resubmit-field__label"
                htmlFor="project-resubmit-notes"
              >
                What changed in this version?{' '}
                <span className="project-resubmit-field__optional">
                  (Optional)
                </span>
              </label>
              <textarea
                id="project-resubmit-notes"
                className="project-resubmit-field__textarea"
                placeholder="e.g. Added a stakeholder section, tightened the conclusion..."
                value={editedChangeNotes}
                maxLength={300}
                onChange={(event) => setEditedChangeNotes(event.target.value)}
              />
              <div className="project-resubmit-field__counter">
                {editedChangeNotes.length}/300
              </div>
            </div>

            <div className="project-resubmit-upload">
              <div className="project-resubmit-upload__title">
                Upload PDF File{' '}
                <span className="project-resubmit-field__optional">
                  (Optional)
                </span>
              </div>
              <p className="project-resubmit-upload__subtitle">
                Upload a PDF file for AI evaluation. Only text-based PDFs are
                supported — scanned images, slides, or image-only PDFs cannot be
                parsed.
              </p>

              <button
                type="button"
                className={`project-resubmit-upload__zone ${
                  isEditUploadDragging
                    ? 'project-resubmit-upload__zone--dragging'
                    : ''
                }`}
                onClick={handleOpenEditFilePicker}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsEditUploadDragging(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setIsEditUploadDragging(false);
                }}
                onDrop={handleEditUploadDrop}
              >
                <Upload size={24} strokeWidth={1.8} />
                <span>Drag and drop your file here.</span>
                <span className="project-resubmit-upload__browse">
                  or click to browse files
                </span>
              </button>

              <input
                ref={editFileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="project-resubmit-upload__input"
                onChange={handleEditFileInputChange}
              />

              {editedFiles.length > 0 ? (
                <div className="project-resubmit-selected-file">
                  <div>
                    <div className="project-resubmit-selected-file__name">
                      {editedFiles[0].name}
                    </div>
                    <div className="project-resubmit-selected-file__meta">
                      {(editedFiles[0].size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    type="button"
                    className="project-resubmit-selected-file__remove"
                    onClick={handleRemoveEditedFile}
                  >
                    Remove
                  </button>
                </div>
              ) : null}
            </div>

            <div className="project-resubmit-tip">
              {isPdfResubmission
                ? 'PDF upload selected. Remove the file if you want to write essay text instead.'
                : 'You don\'t need to add both text and files. Just add whichever you prefer.'}
            </div>
          </section>

          {!isPdfResubmission ? (
            <section className="project-resubmit-card project-resubmit-card--essay">
              <div className="project-resubmit-card__heading">
                <FileText size={18} strokeWidth={2} />
                <h2>
                  Write Essay Text{' '}
                  <span className="project-resubmit-field__optional">
                    (Optional)
                  </span>
                </h2>
              </div>

              <p className="project-resubmit-card__hint">
                Write your revised essay directly here, or upload a PDF on the left
                instead.
              </p>

              <textarea
                className="project-resubmit-field__textarea project-resubmit-field__textarea--essay"
                placeholder="Paste or type your revised essay..."
                value={editedEssayText}
                maxLength={50000}
                onChange={(event) => setEditedEssayText(event.target.value)}
              />
              <div className="project-resubmit-field__counter">
                {editedEssayText.length}/50000
              </div>
            </section>
          ) : null}
        </div>
      </div>

      <footer className="project-resubmit-footer">
        <button
          type="button"
          className="project-resubmit-button project-resubmit-button--secondary"
          onClick={handleCancelEdit}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="project-resubmit-button project-resubmit-button--primary"
          onClick={handleResubmit}
          disabled={isSubmitting || !canSubmitNewVersion}
        >
          <Send size={14} strokeWidth={2.4} />
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </footer>
    </div>
  );

  const renderSubmissionOverlay = () =>
    isSubmissionProcessingDialogOpen ? (
      <div className="project-submission-overlay">
        <div
          className={`project-submission-overlay__card ${
            submissionOverlayState === 'completed'
              ? 'is-complete'
              : submissionOverlayState === 'failed'
                ? 'is-failed'
                : submissionOverlayState === 'timeout'
                  ? 'is-timeout'
                  : ''
          }`}
        >
          {submissionOverlayState === 'completed' ? (
            <div className="project-submission-overlay__success-icon">
              <Check size={28} strokeWidth={2.5} />
            </div>
          ) : submissionOverlayState === 'failed' ? (
            <div className="project-submission-overlay__status-icon is-failed">
              !
            </div>
          ) : submissionOverlayState === 'timeout' ? (
            <div className="project-submission-overlay__status-icon is-timeout">
              ...
            </div>
          ) : (
            <div className="project-submission-overlay__spinner" />
          )}

          <h3 className="project-submission-overlay__title">
            {submissionOverlayTitle}
          </h3>
          <p className="project-submission-overlay__description">
            {submissionOverlayDescription}
          </p>

          {submissionOverlayState === 'processing' ? (
            <>
              <div className="project-submission-overlay__progress">
                <div
                  className="project-submission-overlay__progress-fill"
                  style={{ width: `${submissionOverlayProgress}%` }}
                />
              </div>
              <div className="project-submission-overlay__stage">
                {submissionOverlayStageLabel}
              </div>
            </>
          ) : null}

          <div className="project-submission-overlay__actions">
            {submissionOverlayState === 'completed' ? (
              <>
                <button
                  type="button"
                  className="project-submission-overlay__button project-submission-overlay__button--ghost"
                  onClick={() => {
                    handleCloseProcessingDialog();
                    navigate('/project');
                  }}
                >
                  Back to Projects
                </button>
                <button
                  type="button"
                  className="project-submission-overlay__button project-submission-overlay__button--primary"
                  onClick={() => {
                    handleCloseProcessingDialog();
                    if (projectId) {
                      navigate(getSubmissionReportUrl(projectId, submissionId));
                    }
                  }}
                >
                  View Report
                  <Send size={14} strokeWidth={2.4} />
                </button>
              </>
            ) : submissionOverlayState === 'failed' ? (
              <button
                type="button"
                className="project-submission-overlay__button project-submission-overlay__button--ghost"
                onClick={handleCloseProcessingDialog}
              >
                Close
              </button>
            ) : submissionOverlayState === 'timeout' ? (
              <>
                <button
                  type="button"
                  className="project-submission-overlay__button project-submission-overlay__button--ghost"
                  onClick={handleCloseProcessingDialog}
                >
                  Stay on Project
                </button>
                <button
                  type="button"
                  className="project-submission-overlay__button project-submission-overlay__button--primary"
                  onClick={() => {
                    handleCloseProcessingDialog();
                    navigate('/project');
                  }}
                >
                  Back to Projects
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    ) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="project-detail-loading">
        <div className="text-center">
          <p className="subtitle-2 text-grey-80">Loading project...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageError || !project) {
    return (
      <div className="project-detail-error">
        <div className="project-detail-error-content">
          <p className="subtitle-2 text-red-500 mb-3">
            {pageError || 'Project not found'}
          </p>
          <Button variant="blue" onClick={() => navigate("/project")}>
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const comparisonKeyImprovements = comparisonAnalysis?.key_improvements ?? [];
  const comparisonKeyRegressions = comparisonAnalysis?.key_regressions ?? [];
  const displayName =
    getFirstName(user?.display_name) || getFirstName(project.owner_name) || 'User';
  const hasExistingSubmission = Boolean(
    submissionId || project.latest_submission_id || submissions.length > 0,
  );
  const welcomeMessage = hasExistingSubmission
    ? project.status === 'Processing'
      ? 'Your submission is being analyzed by ILA.'
      : project.status === 'Failed'
        ? 'Review your project and retry the evaluation.'
        : 'Review your latest submission feedback with ILA.'
    : "Let's begin a new project with ILA!";

  return (
    <div className="project-detail">
      {/* Header Section */}
      <div className="project-detail-header">
        <div className="project-detail-welcome">
          <h3 className="heading-5">Hi, {displayName}!</h3>
          <p className="subtitle-2 text-grey-80">
            {welcomeMessage}
          </p>
        </div>

        <div className="project-detail-actions">
          <Button
            variant="grey"
            onClick={() => navigate(`/project/${projectId}/edit`)}
          >
            ⚙️ Edit Project
          </Button>
          <Button variant="blue" onClick={() => navigate("/project/new")}>
            + Add New Project
          </Button>
        </div>
      </div>

      {submissionError && !isEditMode ? (
        <div className="project-detail-inline-error">{submissionError}</div>
      ) : null}

      {isEditMode ? (
        renderResubmissionComposer()
      ) : (
        <>
      {/* Comparison Selector - Only show if 2+ submissions */}
      {submissions.length >= 2 && (
        <div className="project-detail-comparison">
          <ComparisonSelector
            submissions={submissionOptions}
            leftSubmission={leftSubmission}
            rightSubmission={rightSubmission}
            onLeftChange={setLeftSubmission}
            onRightChange={setRightSubmission}
          />
        </div>
      )}

      {/* Comparison Analysis Banner - Show when comparison data exists */}
      {comparisonAnalysis && (
        <div className="comparison-analysis-banner">
          <div
            className={`comparison-badge ${comparisonAnalysis.overall_improvement}`}
          >
            {comparisonAnalysis.overall_improvement === "improved" && "📈"}
            {comparisonAnalysis.overall_improvement === "regressed" && "📉"}
            {comparisonAnalysis.overall_improvement === "unchanged" && "➡️"}
            <span className="ml-2">
              {comparisonAnalysis.overall_improvement === "improved" &&
                "Improved!"}
              {comparisonAnalysis.overall_improvement === "regressed" &&
                "Needs Work"}
              {comparisonAnalysis.overall_improvement === "unchanged" &&
                "Consistent"}
            </span>
          </div>
          <p className="comparison-summary">{comparisonAnalysis.summary}</p>
          {comparisonKeyImprovements.length > 0 && (
            <div className="comparison-improvements">
              <span className="font-medium text-green-600">Improved: </span>
              {comparisonKeyImprovements.join(", ")}
            </div>
          )}
          {comparisonKeyRegressions.length > 0 && (
            <div className="comparison-regressions">
              <span className="font-medium text-red-600">Needs focus: </span>
              {comparisonKeyRegressions.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Main Content - Three Column Layout */}
      {aiFeedback ? (
        <div className="project-detail-content">
          {/* Column 1: Radar + Feedback Cards */}
          <div className="project-detail-col-1">
            {/* Radar Chart */}
            <div className="detail-card radar-section">
              <div className="flex justify-between items-center mb-4">
                <h4 className="subtitle-1">Radar Chart</h4>
                {selectedDimension && (
                  <span className="caption text-grey-55">
                    Selected: {selectedDimension}
                  </span>
                )}
              </div>
              {radarDimensions.length > 0 ? (
                <InstructorRadarChart
                  className="project-detail-radar-chartjs"
                  datasets={radarDatasets}
                  emptyMessage="No dimension data available"
                  height={450}
                  labels={radarLabels}
                  maxScore={3}
                  onPointClick={handleRadarPointClick}
                />
              ) : (
                <div className="text-center text-grey-55 caption py-8">
                  No dimension data available
                </div>
              )}
            </div>

            {/* Feedback Cards */}
            <div className="feedback-cards-section">
              {selectedDimension ? (
                aiFeedback.dimensions
                  .filter((dim) => dim.name === selectedDimension)
                  .map((dim, idx) => {
                    const improvementSuggestion = getImprovementSuggestion(
                      dim.name,
                      dim.score,
                    );

                    return (
                      <div key={idx} className="feedback-with-suggestion">
                        <DimensionFeedbackCard
                          dimensionLabel={dim.name}
                          dimensionColor={dim.color}
                          level={dim.score}
                          feedbackText={dim.feedback}
                        />

                        {improvementSuggestion && (
                          <>
                            <div className="suggestion-arrow">
                              <svg
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <path
                                  d="M16 4L16 28M16 28L8 20M16 28L24 20"
                                  stroke="#000000"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="improvement-suggestion-card">
                              <div className="suggestion-header">
                                <DimensionLabel
                                  text={dim.name}
                                  color={dim.color}
                                  size="medium"
                                />
                                <span className="body-2">:</span>
                              </div>
                              <p className="suggestion-body caption">
                                {improvementSuggestion}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })
              ) : (
                <div className="feedback-instruction">
                  <p className="subtitle-2 text-grey-80">
                    👆 Click a dimension on the radar chart
                  </p>
                  <p className="caption text-grey-55">
                    Select any dimension to view detailed feedback
                  </p>
                </div>
              )}
            </div>

            {/* View Full Report Button */}
            <Button
              variant="blue"
              onClick={() => {
                if (projectId) {
                  navigate(getSubmissionReportUrl(projectId, submissionId));
                }
              }}
              className="w-full"
            >
              Full Report
            </Button>
          </div>

          {/* Column 2: Submission Content */}
          <div className="project-detail-col-2">
            <div className="submission-content-card">
              <div className="flex justify-between items-center mb-3">
                <h4 className="subtitle-1">
                  Submission {currentIteration} of {totalSubmissions}
                </h4>
              </div>

              {/* Show submitter attribution for the current submission */}
              {(() => {
                const currentSub = submissions.find(
                  (s) => s.id === submissionId,
                );
                if (currentSub?.submitted_by_name) {
                  return (
                    <p className="caption text-grey-55 mb-3">
                      Submitted by {currentSub.submitted_by_name} on{" "}
                      {formatEnglishDateTime(currentSub.submitted_at, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  );
                }
                return null;
              })()}

              {submissionContent ? (
                <>
                  <p className="submission-text body-2">{submissionContent}</p>
                  <div className="submission-actions">
                    <Button
                      variant="grey"
                      onClick={handleEditClick}
                      disabled={isSubmitting}
                    >
                      📝 Submit New Version
                    </Button>
                  </div>
                </>
              ) : (
                <div className="submission-empty">
                  <p className="subtitle-2">No submission content available</p>
                  <p className="caption">
                    Submit your project to view content here
                  </p>
                </div>
              )}
            </div>

            {/* Submission History - shows all versions with submitter info for group projects */}
            {project.project_type === "group" && submissions.length > 0 && (
              <div className="detail-card mt-4">
                <h4 className="subtitle-1 mb-3">Submission History</h4>
                <div className="flex flex-col gap-2">
                  {submissions.map((sub) => (
                    <div
                      key={sub.id}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        sub.id === submissionId
                          ? "bg-blue-50 border border-blue-200"
                          : "hover:bg-gray-50 border border-transparent"
                      }`}
                      onClick={() => {
                        setSubmissionId(sub.id);
                        setCurrentIteration(sub.iteration_number);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="body-2 font-medium">
                          Submission {sub.iteration_number}
                        </span>
                        {sub.submitted_by_name && (
                          <span className="caption text-grey-55">
                            Submitted by {sub.submitted_by_name}
                          </span>
                        )}
                        {sub.change_notes ? (
                          <span className="caption text-grey-55">
                            {sub.change_notes}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="caption text-grey-55">
                          {formatEnglishDateTime(sub.submitted_at, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        <span
                          className={`caption ${
                            sub.ai_processing_status === "completed"
                              ? "text-green-600"
                              : sub.ai_processing_status === "failed"
                                ? "text-red-500"
                                : "text-yellow-600"
                          }`}
                        >
                          {sub.ai_processing_status === "completed"
                            ? "✓ Evaluated"
                            : sub.ai_processing_status === "failed"
                              ? "✗ Failed"
                              : "⏳ Processing"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructor Suggestion Card */}
            {instructorSuggestion && (
              <div className="detail-card mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">💡</span>
                  <h4 className="subtitle-1">Instructor Suggestion</h4>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                  <p className="body-2 text-gray-800 whitespace-pre-wrap">
                    {instructorSuggestion.suggestion_text}
                  </p>
                  <div className="flex justify-end mt-3">
                    <span className="caption text-grey-55">
                      Updated:{" "}
                      {formatEnglishDateTime(instructorSuggestion.updated_at)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : project.status === "Processing" ? (
        <div className="processing-state">
          <div className="processing-state-icon">⏳</div>
          <h5 className="subtitle-1 mb-2">AI Evaluation in Progress</h5>
          <p className="body-2 text-grey-80 mb-2">
            {processingStatus ||
              "Your project is being analyzed by our AI system. This typically takes 30-60 seconds."}
          </p>
          {processingStatus.includes("longer than expected") && (
            <p className="caption text-orange-600 mb-4">
              The analysis is taking longer than usual. You can safely leave
              this page and return later.
            </p>
          )}
          <div className="flex justify-center gap-3 mt-4">
            <Button variant="grey" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      ) : project.status === "Failed" ? (
        <div className="processing-state">
          <div className="processing-state-icon">❌</div>
          <h5 className="subtitle-1 mb-2">AI Evaluation Failed</h5>
          <p className="body-2 text-grey-80 mb-4">
            {project.ai_processing_error ||
              "An error occurred while analyzing your project. Please try again."}
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              variant="blue"
              onClick={handleRetryEvaluation}
              disabled={isRetryingEvaluation}
            >
              {isRetryingEvaluation ? "Retrying..." : "Retry Evaluation"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="processing-state">
          <div className="processing-state-icon">📋</div>
          <h5 className="subtitle-1 mb-2">No Feedback Yet</h5>
          <p className="body-2 text-grey-80 mb-4">
            This project doesn&apos;t have AI feedback available. This may
            happen if the project was created before AI evaluation was enabled.
          </p>
          <Button
            variant="blue"
            onClick={handleSubmitProject}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit for AI Feedback"}
          </Button>
        </div>
      )}

      {/* Floating Chat Bubble */}
      <div className="chat-floating-container">
        {isChatOpen ? (
          <div className="chat-floating-panel">
            <div className="chat-panel-header">
              <h4>Ask about your feedback</h4>
              <button
                className="chat-close-btn"
                onClick={() => setIsChatOpen(false)}
                aria-label="Close chat"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="chat-panel-body">
              <ChatInterface
                messages={chatMessages}
                onSendMessage={handleSendMessage}
                isLoading={isChatLoading}
                placeholder="Ask about your feedback..."
              />
            </div>
          </div>
        ) : (
          <button
            className="chat-bubble-btn"
            onClick={() => setIsChatOpen(true)}
            aria-label="Open chat"
          >
            <svg
              className="chat-bubble-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
        </>
      )}
      {renderSubmissionOverlay()}
    </div>
  );
};

export default ProjectDetail;
